import { setStatus } from './status'
import { getTurnstileToken, isTurnstileEnabled, onTurnstileChange, resetTurnstile } from './turnstile'
import { applyServerFieldErrors, collectFormValues, validateAll } from './validation'

// Stamped once when the form code loads — used by the server's
// minimum-time-to-submit bot check.
const formLoadedAt = Date.now()

let inFlight = false

interface SubmitResponse {
  ok?: boolean
  error?: string
  fieldErrors?: Record<string, string>
}

export function initSubmit(form: HTMLFormElement): void {
  const submitBtn = document.getElementById('submitBtn') as HTMLButtonElement

  // Hard-lock the submit button on Turnstile: it stays disabled until the
  // visitor completes the challenge, and re-locks if the token expires, errors,
  // or is reset after a failed submit. Turnstile initializes after this runs
  // (it waits on the runtime-config fetch), so we register unconditionally and
  // it pushes the locked state once active. With no Turnstile configured (bare
  // local dev, no site key) the button stays enabled and the server enforces.
  onTurnstileChange(() => updateSubmitState(submitBtn))
  updateSubmitState(submitBtn)

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    void handleSubmit(form, submitBtn)
  })
}

/** Single source of truth for the button: locked while submitting or unsolved. */
function updateSubmitState(submitBtn: HTMLButtonElement): void {
  submitBtn.disabled = inFlight || (isTurnstileEnabled() && !getTurnstileToken())
}

async function handleSubmit(form: HTMLFormElement, submitBtn: HTMLButtonElement): Promise<void> {
  if (inFlight) return // double-submit guard
  if (!validateAll(form)) return

  if (isTurnstileEnabled() && !getTurnstileToken()) {
    setStatus('Please complete the verification challenge above, then submit again.', true)
    return
  }

  inFlight = true
  updateSubmitState(submitBtn)
  setStatus('Submitting…')

  try {
    const payload = {
      ...collectFormValues(form),
      turnstileToken: getTurnstileToken(),
      formLoadedAt,
    }

    const res = await fetch('/api/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = (await res.json().catch(() => ({}))) as SubmitResponse

    if (res.ok && data.ok) {
      showSuccess(form)
      return
    }

    if (data.fieldErrors && applyServerFieldErrors(form, data.fieldErrors)) {
      setStatus('Please correct the highlighted fields and submit again.', true)
    } else {
      setStatus(data.error ?? 'Unable to submit form. Please try again.', true)
    }
    resetTurnstile() // tokens are single-use
  } catch {
    setStatus('Unable to submit form. Please check your connection and try again.', true)
    resetTurnstile()
  } finally {
    inFlight = false
    updateSubmitState(submitBtn)
  }
}

function showSuccess(form: HTMLFormElement): void {
  const success = document.getElementById('successPage')
  if (!success) return

  form.style.display = 'none'
  success.hidden = false
  success.classList.add('visible')

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' })

  const title = success.querySelector<HTMLElement>('.success-title')
  title?.focus({ preventScroll: true })
}
